import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import financialSquare from './../../assets/images/financial-shapes-square.svg';
import financialCircle from './../../assets/images/financial-shapes-circle.svg';
import financialTriangle from './../../assets/images/financial-shapes-triangle.svg';
import financialMetricsBarChart from './../../assets/images/financial-metrics-bar-chart.svg';
import './../financial.scss';
import AOS from "aos";
import { gsap } from 'gsap';
import "aos/dist/aos.css";

export default function FinancialMetrics() {

  const squareRef = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.height = fill + '%';
          }

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }

        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(squareRef.current, 0.5, {rotation:"+=90"})
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); // <- cleanup!

    AOS.init({
      once: true
    });

    AOS.refresh();

  }, []);

  return (
    <>
      <div className="financial-metrics">

        <p id="s1" className="intro" data-aos="fade-up">Discover how financial companies track and measure direct mail.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-right'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Checking Mail Effectiveness</h3>
                
                <div className='progress-bar-area vertical-bar-chart' data-aos data-aos-id="bar-anim" data-aos-offset="300">
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="47"><span class="percentage" data-to="47" aria-hidden="true">0</span><span class="screen-reader-text">47</span>%</div>
                    </div>
                    <span className='label'>Always</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress' data-fill="45"><span class="percentage" data-to="45" aria-hidden="true">0</span><span class="screen-reader-text">45</span>%</div>
                    </div>
                    <span className='label'>Most times</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="7"><span class="percentage" data-to="7" aria-hidden="true">0</span><span class="screen-reader-text">7</span>%</div>
                    </div>
                    <span className='label'>Sometimes</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="1"><span class="percentage" data-to="1" aria-hidden="true">0</span><span class="screen-reader-text">1</span>%</div>
                    </div>
                    <span className='label'>Never</span>
                  </div>
                </div>

                <p>Out of all industries, financial companies are the most likely to check the effectiveness of direct mail.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Measuring direct mail effectiveness is crucial for shaping future campaigns. By better understanding what kind of messaging, design, offers and formats work best, brands can create more effective and actionable marketing.</p>
              </div>
            </div>

            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Measuring Mail Impact</h3>
                <div data-aos="horz-bar-chart" data-aos-id="bar-anim" className='progress-bar'>
                  <div className='progress' data-fill="56"><span class="percentage" data-to="56" aria-hidden="true">0</span><span class="screen-reader-text">56</span>%</div>
                </div>
                <p>Response rate</p>
                <p className="no-margin-bottom"><strong>55%</strong> — Direct attribution</p>
                <p><strong>53%</strong> — Conversion</p>
                <p>Financial companies typically measure the impact of direct mail by tracking response rates, direct attribution or conversion.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>To calculate direct mail response rate, simply divide the number of responses or interactions received by the number of mailpieces sent out.</p>
              </div>
            </div>
          </div>

          <div className='col-left'>

            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail Response Rates</h3>
                <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                  <div className='progressbar-item'>
                    <span className='label'>Less than 5%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="8"><span class="percentage" data-to="8" aria-hidden="true">0</span><span class="screen-reader-text">8</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 6% to 10%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="11"><span class="percentage" data-to="11" aria-hidden="true">0</span><span class="screen-reader-text">11</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 11% to 15%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress' data-fill="28"><span class="percentage" data-to="28" aria-hidden="true">0</span><span class="screen-reader-text">28</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 16% to 20%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="21"><span class="percentage" data-to="21" aria-hidden="true">0</span><span class="screen-reader-text">21</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 21% to 25%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="14"><span class="percentage" data-to="14" aria-hidden="true">0</span><span class="screen-reader-text">14</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>More than 25%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="15"><span class="percentage" data-to="15" aria-hidden="true">0</span><span class="screen-reader-text">15</span>%</div>
                    </div>
                  </div>
                </div>
                <p>Most financial companies report direct mail response rates of 11%–15%. After health care businesses, financial companies are the second-most likely to have response rates of more than 25%, with 15% of financial companies reporting so.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p id="s2">Make it quick and easy for customers to respond to your mailpiece. Including a QR Code<sup>&reg;</sup>,<a href="#reference-2" class="cm-article-content__reference" aria-label="Footnote 2" title="Footnote 2"><sup aria-hidden="true">[2]</sup></a> a telephone number unique to your mail campaign, or a <strong><a href="/../6-ways-to-measure-your-next-direct-mail-campaign/">personalized URL (PURL)</a></strong> can create a seamless experience that encourages customers to engage.</p>
              </div>
            </div>

            <div className='personalized-box secondary'>
              <h3>Financial businesses typically measure the impact of direct mail via response rates.</h3>
              <div className='shape-images'>
                <div className='square'>
                  <img ref={squareRef} src={financialSquare} alt="" />
                </div>
                <div className='circle'>
                  <img src={financialCircle} alt="" />
                </div>
                <div className='triangle triangle-1'>
                  <img ref={triangle1Ref} src={financialTriangle} alt="" />
                </div>
                <div className='triangle triangle-2'>
                  <img ref={triangle2Ref} src={financialTriangle} alt="" />
                </div>
              </div>
            </div>


          </div>
        </div>

      </div>
    </>
  );
}
